import { Grid, Typography, Box, Button } from '@mui/material';
import { useFormikContext, getIn } from 'formik';
import { ParticipationLimits } from '../../../components/assignPromotionFlowComponent/ParticipationLimits';
import { AlwaysWinLimits } from '../../../components/assignPromotionFlowComponent/AlwaysWinLimits';
import { WinningLimits } from './WinningLimits';
import { usePrompt } from '../../../common/PromptOnChanges/promptHelper';
import { useLayoutEffect, useState, Dispatch, SetStateAction } from 'react';
import { limitsStateValues } from '../../../types/componentTypes/limits';
import { prizeDataType } from '../EditPromotionPage';
import { isEqual as _isEqual } from 'lodash';

let tiersList = [1, 2, 3, 4, 5];

interface LimitsFormProps {
    handleTabChange: Dispatch<SetStateAction<boolean>>;
    prizesData: Array<prizeDataType>;
    promoFlow: string;
}

const isFormUnchanged = (formikValues: any, initialFormikValues: any) => {
    return _isEqual(formikValues, initialFormikValues);
};

const LimitsForm = ({ handleTabChange, prizesData, promoFlow }: LimitsFormProps) => {
    const [changesApplied, setChangesApplied] = useState(false);
    const formik = useFormikContext<limitsStateValues>();
    const isInstantWin = !!formik.values.flow?.instantWin;
    const isCnG = !!formik.values.flow?.autoRedeemCnG || !!formik.values.flow?.redeemPincodeForCurrencies;

    useLayoutEffect(() => {
        const filteredValues = { ...formik.values };
        const filteredInitialValues = { ...formik.initialValues };

        delete filteredValues.selectedDateLimitOption;
        delete filteredInitialValues.selectedDateLimitOption;

        const hasChanges = !isFormUnchanged(filteredValues, filteredInitialValues);

        const differences: Record<string, { initial: any; current: any }> = {};
        Object.keys(filteredValues).forEach((key) => {
            if (JSON.stringify(getIn(filteredValues, key)) !== JSON.stringify(getIn(filteredInitialValues, key))) {
                differences[key] = {
                    initial: getIn(filteredInitialValues, key),
                    current: getIn(filteredValues, key),
                };
            }
        });

        setChangesApplied(hasChanges);
        handleTabChange(hasChanges);
    }, [formik.values, formik.initialValues, handleTabChange]);

    usePrompt('This will discard all the changes. Are you sure?', changesApplied);

    return (
        <>
            <Grid container columnSpacing={6} className='limits-form-container'>
                <Grid item xs={5}>
                    <Box>
                        <Typography variant='h5' className='limits-form-items'>
                            Participation Limits
                        </Typography>
                        <ParticipationLimits
                            formik={formik}
                            useTitles={false}
                            limitsBoxClass={'limits-participation-box'}
                            startEndDateColumns={4.5}
                            timeZoneColumns={4.5}
                            containerSpacing={2}
                            formClass='form-control-participation-limits'></ParticipationLimits>
                    </Box>
                    {formik.values.flow?.instantWin?.params?.alwaysWin &&
                        <Box>
                            <Typography variant='h5' className='limits-form-items alwaysWin'>
                                Always Win Limits
                            </Typography>
                            <AlwaysWinLimits
                                formik={formik}
                                useTitles={false}
                                limitsBoxClass={'limits-alwaysWin-box'}
                                startEndDateColumns={4.5}
                                timeZoneColumns={4.5}
                                containerSpacing={2}
                                prizesData={prizesData}
                                formClass='form-control-alwaysWin-limits'
                            />
                        </Box>
                    }
                </Grid>
                {(isInstantWin || isCnG) &&
                    <Grid item xs={5}>
                        <Typography variant='h5' className='limits-form-items'>
                            {isCnG ? 'Redemption Limits' : 'Winning Limits'}
                        </Typography>
                        <WinningLimits
                            isInstantWin={isInstantWin}
                            isCnG={isCnG}
                            promoFlow={promoFlow}
                            formik={formik}
                            tiersList={tiersList}
                        />
                    </Grid>
                }
            </Grid>
            <Box className='changesButtons'>
                {changesApplied && (
                    <Button
                        variant='outlined'
                        onClick={() => formik.resetForm({ values: formik.initialValues })}
                    >
                        Discard
                    </Button>
                )}
                <Button variant='contained' color='primary' type='submit'>
                    Save
                </Button>
            </Box>
        </>
    );
};

export { LimitsForm };
